import { AxiosResponse, Canceler } from 'axios';
import {
  IApiResult,
  IPaginationResponseDto,
  IFlowKey,
  IOnboardingLane,
  IOnboardingFlow,
  IOnboardingCollectionMapper,
  IOnboardingFlowLaneMapper,
  ISearchLaneResponseDto,
} from '../types';
import settings from '../settings';
import api from './base';

export function getDistinctFlowKeys(): Promise<AxiosResponse<IFlowKey[]>> {
  const uri = `${settings.getApiURI()}/onboarding-admin/v1/distinctFlowKeys`;

  return api.http.get(uri);
}

export function fetchLanes(page = 1, limit?: number): [Promise<AxiosResponse<IPaginationResponseDto<IOnboardingLane>>>, Canceler] {
  const uri = `${settings.getApiURI()}/onboarding-admin/v1/lanes`;

  return api.getWithCancel(uri, {
    params: {
      page,
      limit,
    },
  });
}

export function fetchLanesByTitle(keyword: string): [Promise<AxiosResponse<ISearchLaneResponseDto<IOnboardingLane>>>, Canceler] {
  const uri = `${settings.getApiURI()}/search/v2/lanes`;

  return api.getWithCancel(uri, {
    params: {
      keyword,
      page: 1,
      limit: 20,
    },
  });
}

export function fetchLane(laneId: number): [Promise<AxiosResponse<IOnboardingLane>>, Canceler] {
  const uri = `${settings.getApiURI()}/onboarding-admin/v1/lanes/${laneId}`;

  return api.getWithCancel(uri);
}

export function createLane(lane: Partial<IOnboardingLane>): Promise<AxiosResponse<IApiResult<IOnboardingLane>>> {
  const uri = `${settings.getApiURI()}/onboarding-admin/v1/lanes`;

  return api.http.post(uri, lane);
}

export function updateLane(laneId: number, lane: Partial<IOnboardingLane>): Promise<AxiosResponse<IApiResult<IOnboardingLane>>> {
  const uri = `${settings.getApiURI()}/onboarding-admin/v1/lanes/${laneId}`;

  return api.http.patch(uri, lane);
}

export function updateLaneCollections(
  laneId: number,
  mappers: Partial<IOnboardingCollectionMapper>[],
): Promise<AxiosResponse<IApiResult<IOnboardingLane>>> {
  const uri = `${settings.getApiURI()}/onboarding-admin/v1/lanes/${laneId}/collection-mappers`;

  return api.http.put(uri, { collectionMappers: mappers });
}

export function deleteLane(laneId: number): Promise<AxiosResponse<IApiResult<IOnboardingLane>>> {
  const uri = `${settings.getApiURI()}/onboarding-admin/v1/lanes/${laneId}`;

  return api.http.delete(uri);
}

export function duplicateLane(laneId: number): Promise<AxiosResponse<IApiResult<IOnboardingLane>>> {
  const uri = `${settings.getApiURI()}/onboarding-admin/v1/lanes/${laneId}/duplicate`;

  return api.http.post(uri);
}

export function fetchFlows(page = 1, limit?: number): [Promise<AxiosResponse<IPaginationResponseDto<IOnboardingFlow>>>, Canceler] {
  const uri = `${settings.getApiURI()}/onboarding-admin/v1/flows`;

  return api.getWithCancel(uri, {
    params: {
      page,
      limit,
    },
  });
}

export function fetchFlow(flowId: number): [Promise<AxiosResponse<IOnboardingFlow>>, Canceler] {
  const uri = `${settings.getApiURI()}/onboarding-admin/v1/flows/${flowId}`;

  return api.getWithCancel(uri);
}

export function createFlow(flow: Partial<IOnboardingFlow>): Promise<AxiosResponse<IApiResult<IOnboardingFlow>>> {
  const uri = `${settings.getApiURI()}/onboarding-admin/v1/flows`;

  return api.http.post(uri, flow);
}

export function updateFlow(flowId: number, flow: Partial<IOnboardingFlow>): Promise<AxiosResponse<IApiResult<IOnboardingFlow>>> {
  const uri = `${settings.getApiURI()}/onboarding-admin/v1/flows/${flowId}`;

  return api.http.patch(uri, flow);
}

export function updateFlowMappers(
  flowId: number,
  mappers: Partial<IOnboardingFlowLaneMapper>[],
): Promise<AxiosResponse<IApiResult<IOnboardingLane>>> {
  const uri = `${settings.getApiURI()}/onboarding-admin/v1/flows/${flowId}/lane-mappers`;

  return api.http.put(uri, { laneMappers: mappers });
}

export function deleteFlow(flowId: number): Promise<AxiosResponse<IApiResult<IOnboardingFlow>>> {
  const uri = `${settings.getApiURI()}/onboarding-admin/v1/flows/${flowId}`;

  return api.http.delete(uri);
}

export function duplicateFlow(flowId: number): Promise<AxiosResponse<IApiResult<IOnboardingFlow>>> {
  const uri = `${settings.getApiURI()}/onboarding-admin/v1/flows/${flowId}/duplicate`;

  return api.http.post(uri);
}
